// src/services/exit.service.js
// Exit Management Service - Frontend API calls

import { api } from './api';

class ExitService {
    /**
     * Initiate exit process for an employee
     * @param {Object} exitData - Exit request data
     * @returns {Promise<Object>} Exit request response with validations
     */
    async initiateExit(exitData) {
        try {
            const response = await api.post('/api/admin/exits/initiate', exitData);
            return response.data;
        } catch (error) {
            console.error('Error initiating exit:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get all exit requests with optional filters
     * @param {Object} filters - { status, employee_id, from_date, to_date }
     * @returns {Promise<Array>} List of exit requests
     */
    async getAllExits(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.employee_id) params.append('employee_id', filters.employee_id);
            if (filters.from_date) params.append('from_date', filters.from_date);
            if (filters.to_date) params.append('to_date', filters.to_date);

            const response = await api.get(`/api/admin/exits?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching exit requests:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get detailed exit request information
     * @param {string} exitRequestId - Exit request UUID
     * @returns {Promise<Object>} Exit request details with clearances and settlement
     */
    async getExitDetails(exitRequestId) {
        try {
            const response = await api.get(`/api/admin/exits/${exitRequestId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching exit details:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Approve or reject an exit request
     * @param {string} exitRequestId - Exit request UUID
     * @param {Object} approvalData - { action: 'approved'|'rejected', approved_exit_date, comments }
     * @returns {Promise<Object>} Updated exit request
     */
    async approveOrRejectExit(exitRequestId, approvalData) {
        try {
            const response = await api.put(`/api/admin/exits/${exitRequestId}/approve`, approvalData);
            return response.data;
        } catch (error) {
            console.error('Error approving/rejecting exit:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Update clearance status for a department
     * @param {string} exitRequestId - Exit request UUID
     * @param {string} department - Department name (IT, HR, Finance, etc.)
     * @param {Object} clearanceData - { checklist_items: array, notes }
     * @returns {Promise<Object>} Updated clearance status
     */
    async updateClearance(exitRequestId, department, clearanceData) {
        try {
            const response = await api.put(
                `/api/admin/exits/${exitRequestId}/clearance/${department}`,
                clearanceData
            );
            return response.data;
        } catch (error) {
            console.error('Error updating clearance:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Calculate final settlement for exiting employee
     * @param {string} exitRequestId - Exit request UUID
     * @param {Object} settlementData - Settlement calculation parameters
     * @returns {Promise<Object>} Calculated settlement with breakdown
     */
    async calculateSettlement(exitRequestId, settlementData) {
        try {
            const response = await api.post(
                `/api/admin/exits/${exitRequestId}/settlement/calculate`,
                settlementData
            );
            return response.data;
        } catch (error) {
            console.error('Error calculating settlement:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Complete exit process (set employee to inactive, disable login)
     * @param {string} exitRequestId - Exit request UUID
     * @returns {Promise<Object>} Completion confirmation
     */
    async completeExit(exitRequestId) {
        try {
            const response = await api.post(`/api/admin/exits/${exitRequestId}/complete`);
            return response.data;
        } catch (error) {
            console.error('Error completing exit:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get exit request by employee ID (Admin only)
     * @param {string} employeeId - Employee ID
     * @returns {Promise<Object>} Active exit request for employee
     */
    async getExitByEmployee(employeeId) {
        try {
            const response = await api.get(`/api/admin/exits/employee/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching exit request:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch exit request'
            };
        }
    }

    /**
     * Get current logged-in user's own exit request (Employee accessible)
     * @returns {Promise<Object>} Active exit request for current user
     */
    async getMyExitRequest() {
        try {
            const response = await api.get('/api/exits/my-exit');
            return response.data;
        } catch (error) {
            console.error('Error fetching my exit request:', error);

            // Check if 403 response contains exitInfo (for disabled/exited employees)
            if (error.response?.status === 403 && error.response?.data?.exitInfo) {
                return {
                    success: true,
                    data: {
                        ...error.response.data.exitInfo,
                        _fromAuthError: true,
                        _accountDisabled: true
                    }
                };
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch exit request'
            };
        }
    }

    /**
     * Cancel exit request
     * @param {string} exitRequestId - Exit request UUID
     * @param {string} reason - Reason for cancellation
     * @returns {Promise<Object>} Cancellation confirmation
     */
    async cancelExit(exitRequestId, reason) {
        try {
            const response = await api.post(`/api/admin/exits/${exitRequestId}/cancel`, {
                cancel_reason: reason
            });
            return response.data;
        } catch (error) {
            console.error('Error cancelling exit:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to cancel exit'
            };
        }
    }

    /**
     * Update exit dates
     * @param {string} exitRequestId - Exit request UUID
     * @param {Object} dateData - { resignation_date, requested_exit_date, update_reason }
     * @returns {Promise<Object>} Update confirmation
     */
    async updateExitDates(exitRequestId, { resignation_date, requested_exit_date, update_reason }) {
        try {
            const response = await api.put(`/api/admin/exits/${exitRequestId}/update-dates`, {
                resignation_date,
                requested_exit_date,
                update_reason
            });
            return response.data;
        } catch (error) {
            console.error('Error updating exit dates:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update exit dates'
            };
        }
    }

    /**
     * Get exit statistics for dashboard
     * @returns {Promise<Object>} Exit statistics
     */
    async getExitStatistics() {
        try {
            const response = await api.get('/api/admin/exits?status=pending_approval');
            const pendingApprovals = response.data.data || [];

            const responseApproved = await api.get('/api/admin/exits?status=approved');
            const activeExits = responseApproved.data.data || [];

            const responseCompleted = await api.get('/api/admin/exits?status=completed');
            const completedExits = responseCompleted.data.data || [];

            return {
                pending_approvals: pendingApprovals.length,
                active_exits: activeExits.length,
                completed_this_month: completedExits.filter(exit => {
                    const completedDate = new Date(exit.completed_at);
                    const now = new Date();
                    return completedDate.getMonth() === now.getMonth() &&
                           completedDate.getFullYear() === now.getFullYear();
                }).length
            };
        } catch (error) {
            console.error('Error fetching exit statistics:', error);
            return {
                pending_approvals: 0,
                active_exits: 0,
                completed_this_month: 0
            };
        }
    }

    /**
     * Check if employee has pending exit request
     * @param {string} employeeId - Employee ID
     * @returns {Promise<Object|null>} Pending exit request or null
     */
    async getPendingExitForEmployee(employeeId) {
        try {
            const response = await api.get(`/api/admin/exits?employee_id=${employeeId}&status=pending_approval`);
            const exits = response.data.data || [];
            return exits.length > 0 ? exits[0] : null;
        } catch (error) {
            console.error('Error checking pending exit:', error);
            return null;
        }
    }

    /**
     * Format exit type for display
     * @param {string} exitType - Exit type code
     * @returns {string} Formatted exit type
     */
    formatExitType(exitType) {
        const types = {
            'resignation': 'Resignation',
            'termination': 'Termination',
            'retirement': 'Retirement',
            'end_of_contract': 'End of Contract',
            'mutual_separation': 'Mutual Separation',
            'absconding': 'Absconding'
        };
        return types[exitType] || exitType;
    }

    /**
     * Format exit status for display
     * @param {string} status - Exit status code
     * @returns {Object} { label, color, icon }
     */
    formatExitStatus(status) {
        const statuses = {
            'initiated': {
                label: 'Initiated',
                color: 'gray',
                icon: '📝'
            },
            'pending_approval': {
                label: 'Pending Approval',
                color: 'yellow',
                icon: '⏳'
            },
            'approved': {
                label: 'Approved',
                color: 'blue',
                icon: '✅'
            },
            'rejected': {
                label: 'Rejected',
                color: 'red',
                icon: '❌'
            },
            'in_clearance': {
                label: 'In Clearance',
                color: 'purple',
                icon: '🔄'
            },
            'clearance_complete': {
                label: 'Clearance Complete',
                color: 'green',
                icon: '✔️'
            },
            'settlement_pending': {
                label: 'Settlement Pending',
                color: 'orange',
                icon: '💰'
            },
            'completed': {
                label: 'Completed',
                color: 'green',
                icon: '🎉'
            },
            'cancelled': {
                label: 'Cancelled',
                color: 'gray',
                icon: '🚫'
            }
        };
        return statuses[status] || { label: status, color: 'gray', icon: '❓' };
    }

    /**
     * Get clearance status color
     * @param {string} status - Clearance status
     * @returns {string} Tailwind color class
     */
    getClearanceStatusColor(status) {
        const colors = {
            'pending': 'gray',
            'in_progress': 'blue',
            'completed': 'green',
            'not_applicable': 'gray'
        };
        return colors[status] || 'gray';
    }

    /**
     * Calculate notice period days remaining
     * @param {string} resignationDate - Resignation date string
     * @param {string} exitDate - Exit date string
     * @param {number} noticePeriodDays - Required notice period days
     * @returns {Object} { daysRemaining, isShortfall, shortfallDays }
     */
    calculateNoticePeriod(resignationDate, exitDate, noticePeriodDays) {
        const resignDate = new Date(resignationDate);
        const exitDt = new Date(exitDate);
        const actualDays = Math.ceil((exitDt - resignDate) / (1000 * 60 * 60 * 24));
        const shortfallDays = Math.max(0, noticePeriodDays - actualDays);

        return {
            actualDays,
            requiredDays: noticePeriodDays,
            daysRemaining: Math.max(0, noticePeriodDays - actualDays),
            isShortfall: actualDays < noticePeriodDays,
            shortfallDays
        };
    }

    /**
     * Handle API errors
     * @private
     */
    handleError(error) {
        if (error.response) {
            // Server responded with error status
            return {
                message: error.response.data.message || 'An error occurred',
                status: error.response.status,
                data: error.response.data
            };
        } else if (error.request) {
            // Request was made but no response
            return {
                message: 'No response from server. Please check your connection.',
                status: 0
            };
        } else {
            // Something else happened
            return {
                message: error.message || 'An unexpected error occurred',
                status: -1
            };
        }
    }
}

export default new ExitService();
